import React from "react";
import { motion } from "framer-motion";

const HydrationGraph: React.FC = () => {
    return (
        <div className="space-y-3" dir="rtl">
            <div>
                <h4 className="font-medium text-[#002B5C] mb-1">وضعیت امروز</h4>
                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="bg-[#ECEFF1] p-3 rounded-[16px] text-center shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                        <div className="text-lg font-medium text-[#00E5FF]">۸۵٪</div>
                        <div className="text-xs text-[#9EA3A9]">سطح آب بدن</div>
                    </div>
                    <div className="bg-[#ECEFF1] p-3 rounded-[16px] text-center shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                        <div className="text-lg font-medium text-[#00E5FF]">۱.۸</div>
                        <div className="text-xs text-[#9EA3A9]">لیتر مصرف شده</div>
                    </div>
                    <div className="bg-[#ECEFF1] p-3 rounded-[16px] text-center shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                        <div className="text-lg font-medium text-[#00E5FF]">۲.۵</div>
                        <div className="text-xs text-[#9EA3A9]">هدف روزانه (لیتر)</div>
                    </div>
                    <div className="bg-[#ECEFF1] p-3 rounded-[16px] text-center shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                        <div className="text-lg font-medium text-[#32D74B]">مطلوب</div>
                        <div className="text-xs text-[#9EA3A9]">وضعیت</div>
                    </div>
                </div>

                <h4 className="font-medium text-[#002B5C] mb-1">پیشرفت امروز</h4>
                <div className="bg-[#ECEFF1] p-3 rounded-[16px] shadow-[0_2px_8px_rgba(0,0,0,0.02)] mb-3">
                    <div className="flex justify-between text-xs text-[#9EA3A9] mb-1">
                        <span>۰ لیتر</span>
                        <span>۲.۵ لیتر</span>
                    </div>
                    <div className="h-4 bg-[#E0F7FA] rounded-full overflow-hidden">
                        <motion.div
                            className="h-full bg-gradient-to-r from-[#00E5FF] to-[#0072CE] rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: "72%" }}
                            transition={{ duration: 1, ease: [0.4, 0.0, 0.2, 1] }}
                        />
                    </div>
                    <div className="flex justify-between items-center mt-2">
                        <div className="text-xs text-[#9EA3A9]">۷۲٪ تکمیل شده</div>
                        <div className="text-xs text-[#00E5FF] font-medium">۰.۷ لیتر باقیمانده</div>
                    </div>
                </div>

                <h4 className="font-medium text-[#002B5C] mb-1">روند هفتگی</h4>
                <div className="h-20 bg-[#ECEFF1] rounded-[16px] p-3 relative shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                    <div className="absolute top-1/4 left-0 right-0 h-0.5 bg-[#32D74B]/50 z-10"></div>

                    <div className="relative h-full flex items-end justify-between">
                        {["ش", "ی", "د", "س", "چ", "پ", "ج"].map((day, i) => {
                            const hydrationPercent = [78, 85, 82, 90, 75, 80, 72];
                            const hydrationHeight = (hydrationPercent[i] / 100) * 100;

                            return (
                                <div key={i} className="flex flex-col items-center w-8">
                                    <div className="relative w-4 flex flex-col items-center">
                                        <motion.div
                                            className="absolute bottom-0 w-4 bg-gradient-to-t from-[#00E5FF] to-[#0072CE] rounded-t-[4px]"
                                            initial={{ height: 0 }}
                                            animate={{ height: `${hydrationHeight}%` }}
                                            transition={{
                                                duration: 0.2,
                                                delay: i * 0.05,
                                                ease: [0.4, 0.0, 0.2, 1],
                                            }}
                                        />
                                        {hydrationPercent[i] >= 80 && (
                                            <motion.div
                                                className="absolute -top-1 w-2 h-2 bg-[#32D74B] rounded-full"
                                                initial={{ scale: 0 }}
                                                animate={{ scale: 1 }}
                                                transition={{
                                                    duration: 0.2,
                                                    delay: i * 0.05 + 0.2,
                                                }}
                                            />
                                        )}
                                    </div>
                                    <span className="text-xs text-[#9EA3A9] mt-1">{day}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="flex justify-between text-xs text-[#9EA3A9] mt-1">
                    <div className="flex items-center">
                        <div className="w-2 h-2 bg-gradient-to-r from-[#00E5FF] to-[#0072CE] rounded-full mr-1"></div>
                        <span>سطح هیدراتاسیون</span>
                    </div>
                    <div className="flex items-center">
                        <div className="w-2 h-2 bg-[#32D74B] rounded-full mr-1"></div>
                        <span>هدف روزانه</span>
                    </div>
                </div>

                <div className="mt-4 p-3 bg-[#ECEFF1] rounded-[16px] shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                    <h4 className="font-medium text-[#002B5C] mb-1">اطلاعات تخمین</h4>
                    <p className="text-sm text-[#4A5568]">تخمین سطح آب بدن از طریق ضربان قلب، دمای بدن و سطح فعالیت انجام شده است.</p>
                    <div className="mt-2 text-xs text-[#9EA3A9]">آخرین به‌روزرسانی: امروز ۱۱:۴۵ صبح</div>

                    <div className="mt-3 bg-[#E0F7FA] p-2 rounded-lg">
                        <div className="flex items-center text-[#00838F]">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-xs font-medium">توصیه</span>
                        </div>
                        <p className="text-xs text-[#00838F] mt-1">برای رسیدن به هدف روزانه، توصیه می‌شود هر ساعت یک لیوان آب بنوشید.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HydrationGraph;
